import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EmailIcon from '@mui/icons-material/Email';
import { DataGrid, GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { IAdminUser, IAdminUserTeamRef, sendWelcomeEmails, useAdminUsersQuery } from '../../api/admin';
import { formatDateTime, relativeDay } from '../../utils/dates';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';
import AccountStatusChip from './AccountStatusChip';
import ProductChips, { enabledProducts } from './ProductChips';
import RecentActivityDialog from './RecentActivityDialog';
export default function UserManagementTable() {
	const navigate = useNavigate();
	const { data: users, isLoading } = useAdminUsersQuery();

	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [sending, setSending] = useState(false);
	const [sent, setSent] = useState(false);
	const [testSend, setTestSend] = useState(false);
	const [activityUser, setActivityUser] = useState<IAdminUser | null>(null);

	const selectedUsers: IAdminUser[] = (users ?? []).filter((u) => selectionModel.includes(u.id));

	function handleOpenConfirm() {
		setSent(false);
		setTestSend(false);
		setConfirmOpen(true);
	}

	function handleClose() {
		setConfirmOpen(false);
		setSent(false);
	}

	async function handleConfirmSend() {
		setSending(true);
		await sendWelcomeEmails(selectedUsers.map((u) => u.id), testSend);
		setSending(false);
		setSent(true);
		setSelectionModel([]);
	}

	const columns: GridColDef[] = [
		{
			field: 'full_name',
			headerName: 'Name',
			flex: 1,
			minWidth: 150,
			maxWidth: 240,
			renderCell: (params: GridRenderCellParams) => (
				<Link
					href={`/admin/users/${params.row.id}`}
					variant="body2"
					onClick={(e: React.MouseEvent) => {
						if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
							e.preventDefault();
							navigate(`/admin/users/${params.row.id}`, {
								state: { userEmail: params.row.email, userName: params.row.full_name },
							});
						}
					}}
					sx={{ textAlign: 'left' }}
				>
					{params.value}
				</Link>
			),
		},
		{
			field: 'organization_names',
			headerName: 'Organization',
			flex: 1,
			minWidth: 140,
			maxWidth: 220,
			valueGetter: ({ row }) =>
				Array.from(new Set(((row.teams ?? []) as IAdminUserTeamRef[]).map((t) => t.organization_name))).join(', '),
		},
		{
			field: 'team_names',
			headerName: 'Team',
			flex: 1,
			minWidth: 120,
			maxWidth: 200,
			valueGetter: ({ row }) => ((row.teams ?? []) as IAdminUserTeamRef[]).map((t) => t.name).join(', '),
		},
		{
			field: 'products',
			headerName: 'Products',
			width: 215,
			sortable: false,
			valueGetter: ({ row }) => enabledProducts(row as IAdminUser).join(', '),
			renderCell: (params: GridRenderCellParams) => <ProductChips user={params.row as IAdminUser} emptyText="" />,
		},
		{
			field: 'account',
			headerName: 'Account',
			width: 105,
			valueGetter: ({ row }) => {
				const user = row as IAdminUser;
				return user.account?.status ?? (user.is_vendor ? 'vendor' : '');
			},
			renderCell: (params: GridRenderCellParams) => {
				const user = params.row as IAdminUser;
				return <AccountStatusChip account={user.account} isVendor={user.is_vendor} />;
			},
		},
		{
			field: 'objects_count',
			headerName: 'Objects',
			width: 90,
			type: 'number',
			valueGetter: ({ row }) => row.views_count + row.tables_count + row.screeners_count,
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip
					title={`${params.row.views_count} views · ${params.row.tables_count} worksheets · ${params.row.screeners_count} screeners`}
				>
					<span>{params.value}</span>
				</Tooltip>
			),
		},
		{
			field: 'mcp_calls_30d',
			headerName: 'MCP Calls',
			width: 120,
			type: 'number',
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip title={`${params.row.mcp_calls_7d} MCP tool calls in the last 7 days · ${params.row.mcp_calls_30d} in the last 30 days`}>
					<span>
						{params.row.mcp_calls_7d} / {params.row.mcp_calls_30d}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'last_activity_date',
			headerName: 'Last Activity',
			width: 115,
			renderCell: (params: GridRenderCellParams) =>
				params.value ? (
					<Tooltip title={`${formatDateTime(params.value)} · click for recent page views`}>
						<Link
							component="button"
							variant="body2"
							onClick={() => setActivityUser(params.row as IAdminUser)}
							sx={{ textAlign: 'left' }}
						>
							{relativeDay(params.value)}
						</Link>
					</Tooltip>
				) : null,
		},
		{
			field: 'created_at',
			headerName: 'Created',
			width: 110,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'welcome_email_sent',
			headerName: 'Welcome Email',
			width: 120,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'email',
			headerName: 'Email',
			flex: 1,
			minWidth: 220,
		},
		{
			field: 'email_domain',
			headerName: 'Domain',
			width: 100,
		},
	];

	return (
		<Box sx={{ height: 'calc(100vh - 310px)', width: '100%' }}>
			{selectionModel.length > 0 && (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
					<Button
						variant="contained"
						size="small"
						startIcon={<EmailIcon />}
						onClick={handleOpenConfirm}
					>
						Send Welcome Email ({selectionModel.length})
					</Button>
				</Box>
			)}
			<DataGrid
				rows={users ?? []}
				columns={columns}
				loading={isLoading}
				density="compact"
				checkboxSelection
				disableSelectionOnClick
				selectionModel={selectionModel}
				onSelectionModelChange={setSelectionModel}
				components={{ Toolbar: AdminQuickFilterToolbar }}
				componentsProps={{ toolbar: { showQuickFilter: true } }}
			/>

			<Dialog
				open={confirmOpen}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { backgroundImage: 'none' } }}
			>
				{sent ? (
					<>
						<DialogTitle>Emails Queued</DialogTitle>
						<DialogContent>
							<Typography variant="body2">
								Welcome email{selectedUsers.length !== 1 ? 's' : ''} queued successfully
								{testSend ? ' — test copy sent to you.' : '.'}
							</Typography>
						</DialogContent>
						<DialogActions>
							<Button variant="contained" onClick={handleClose}>Done</Button>
						</DialogActions>
					</>
				) : (
					<>
						<DialogTitle>Send Welcome Email</DialogTitle>
						<DialogContent>
							<Typography variant="body2" sx={{ mb: 1 }}>
								Send a welcome email for the following {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}?
							</Typography>
							<List dense disablePadding sx={{ mb: 1 }}>
								{selectedUsers.map((u) => (
									<ListItem key={u.id} disableGutters>
										<ListItemText
											primary={u.full_name}
											secondary={u.email}
										/>
									</ListItem>
								))}
							</List>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={testSend}
										onChange={(e) => setTestSend(e.target.checked)}
									/>
								}
								label={
									<Typography variant="caption">
										Send a test copy to myself instead
									</Typography>
								}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleClose} disabled={sending}>Cancel</Button>
							<Button
								variant="contained"
								onClick={handleConfirmSend}
								disabled={sending}
							>
								{sending ? 'Sending…' : 'Send'}
							</Button>
						</DialogActions>
					</>
				)}
			</Dialog>

			<RecentActivityDialog user={activityUser} onClose={() => setActivityUser(null)} />
		</Box>
	);
}
